/**
 * @CODE:ACC-003-DETAIL-001
 * AccommodationDetailPage Component
 *
 * Main page for accommodation details with all components:
 * - Image gallery
 * - Accommodation header with basic info
 * - Amenities grid
 * - Tab navigation (Rooms, Location, Reviews)
 * - Loading skeleton UI
 * - Error handling with 404 page
 * - Responsive layout
 * - Full accessibility support
 */

import { useParams, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, MapPin, Phone, Users, Home, Footprints, Clock, ArrowRight } from "lucide-react";
import { useAccommodationDetail, useAccommodationImages } from "../hooks";
import { AccommodationImageGallery } from "../components/AccommodationImageGallery";
import { AccommodationHeader } from "../components/AccommodationHeader";
import { AmenitiesGrid } from "../components/AmenitiesGrid";
import { RoomsTab } from "../components/RoomsTab";
import { LocationTab } from "../components/LocationTab";
// import { ReviewsTab } from "../components/ReviewsTab"; // Hidden for now
import { AccommodationDetailSkeleton } from "../components/AccommodationDetailSkeleton";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatPrice } from "@/features/accommodation/api";
import { useCourses } from "@/hooks/useCourses";

/**
 * AccommodationDetailPage component
 * Displays complete accommodation details with all related information
 */
/**
 * Format duration from minutes to hours and minutes
 */
const formatDuration = (minutes: number | null): string => {
  if (!minutes) return "정보 없음";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
  }
  return `${mins}분`;
};

/**
 * Format distance
 */
const formatDistance = (km: number | string | null): string => {
  if (km === null || km === undefined) return "정보 없음";
  const kmNum = typeof km === 'string' ? parseFloat(km) : km;
  return `${kmNum}km`;
};

export const AccommodationDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const isMobile = useIsMobile();

  // Fetch accommodation data
  const { data: accommodation, isLoading, isError } = useAccommodationDetail(
    id || ""
  );

  // Fetch all images for carousel
  const { data: accommodationImages } = useAccommodationImages(
    accommodation?.accommodation_id || 0
  );

  // Fetch all courses to find related course
  const { data: courses } = useCourses();

  // Handle invalid/missing ID
  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex flex-col items-center justify-center px-4 py-20">
          <div className="text-center space-y-4 max-w-md">
            <h1 className="text-4xl font-bold text-gray-900">404</h1>
            <h2 className="text-2xl font-semibold text-gray-800">
              숙소를 찾을 수 없습니다
            </h2>
            <p className="text-gray-600">
              요청하신 숙소 정보를 찾을 수 없습니다.
            </p>
            <Button variant="outline" asChild className="mt-4">
              <a href="/stay">목록으로 돌아가기</a>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <AccommodationDetailSkeleton />
        <Footer />
      </div>
    );
  }

  // Handle error state
  if (isError || !accommodation) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex flex-col items-center justify-center px-4 py-20">
          <div className="text-center space-y-4 max-w-md">
            <h1 className="text-4xl font-bold text-gray-900">오류</h1>
            <h2 className="text-2xl font-semibold text-gray-800">
              숙소를 찾을 수 없습니다
            </h2>
            <p className="text-gray-600">
              요청하신 숙소 정보를 불러올 수 없습니다.
            </p>
            <Button variant="outline" asChild className="mt-4">
              <a href="/stay">목록으로 돌아가기</a>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Get all images from accommodation
  // Use fetched images if available, otherwise fallback to main_image
  const allImages = accommodationImages && accommodationImages.length > 0
    ? accommodationImages
    : accommodation.main_image
    ? [accommodation.main_image]
    : [];

  // Find related course based on accommodation's course_id
  const relatedCourse = courses?.find(course => {
    return course.course_id === accommodation.course_id;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-safe">
      <Header />

      {/* Breadcrumbs */}
      <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center flex-wrap gap-2" aria-label="브레드크럼">
            {/* Home */}
            <Link
              to="/"
              className="group flex items-center text-sm transition-all duration-200"
              aria-label="홈으로 이동"
            >
              <div className="flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#F39800] hover:bg-[#F39800]/5 transition-all duration-200">
                <span className="text-gray-600 group-hover:text-[#F39800] font-medium">홈</span>
              </div>
            </Link>

            <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />

            {/* 숙소 찾기 */}
            <Link
              to="/stay"
              className="group flex items-center text-sm transition-all duration-200"
              aria-label="숙소 찾기로 이동"
            >
              <div className="flex items-center px-3 py-1.5 rounded-full bg-white border border-gray-200 hover:border-[#F39800] hover:bg-[#F39800]/5 transition-all duration-200">
                <span className="text-gray-600 group-hover:text-[#F39800] font-medium">숙소 찾기</span>
              </div>
            </Link>

            <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />

            {/* Current Accommodation */}
            <div className="flex items-center px-3 py-1.5 rounded-full bg-[#F39800]/10 border border-[#F39800]/20">
              <MapPin className="w-3.5 h-3.5 text-[#F39800] mr-1.5" />
              <span className="text-[#F39800] font-medium text-sm">{accommodation.name}</span>
            </div>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Desktop/Tablet Layout - 2 Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">

          {/* Left Column - Image and Details (2/3 width on desktop) */}
          <div className="md:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="relative">
              <AccommodationImageGallery
                images={allImages}
                accommodationName={accommodation.name}
                className="w-full rounded-2xl shadow-lg overflow-hidden"
              />
            </div>

            {/* Accommodation Header - Mobile Only */}
            {isMobile && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <AccommodationHeader accommodation={accommodation} />
              </div>
            )}

            {/* Accommodation Header - Desktop */}
            {!isMobile && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <AccommodationHeader accommodation={accommodation} />
              </div>
            )}

            {/* Amenities Section - Only show if amenities exist */}
            {accommodation.amenities && accommodation.amenities.length > 0 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  편의시설
                </h2>
                <AmenitiesGrid
                  amenities={accommodation.amenities}
                  accommodationType={accommodation.accommodation_type}
                />
              </div>
            )}
          </div>

          {/* Right Column - Info Sidebar (1/3 width on desktop) */}
          {!isMobile && (
            <div className="space-y-6">
              {/* Accommodation Info Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">숙소 정보</h3>

                <div className="space-y-4">
                  {/* Price */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-600 font-medium">1박 요금</span>
                    <span className="text-xl font-bold text-[#F39800]">
                      {formatPrice(accommodation.price_from)}
                    </span>
                  </div>

                  {/* Accommodation Type */}
                  {accommodation.accommodation_type && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="text-gray-600 font-medium">숙소 유형</span>
                      <span className="text-lg font-bold text-gray-800">
                        {accommodation.accommodation_type.name}
                      </span>
                    </div>
                  )}

                  {/* Max Guests */}
                  {accommodation.max_guests && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <span className="text-gray-600 font-medium">최대 인원</span>
                      <div className="flex items-center">
                        <Users className="w-4 h-4 text-[#F39800] mr-1" />
                        <span className="text-lg font-bold text-gray-800">
                          {accommodation.max_guests}명
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center mb-2">
                      <MapPin className="w-4 h-4 text-[#F39800] mr-2" />
                      <span className="text-gray-600 font-medium">위치</span>
                    </div>
                    <div className="text-gray-800 font-medium">
                      {accommodation.province} {accommodation.district}
                    </div>
                  </div>

                </div>

                {/* Phone Booking Notice */}
                <div className="mt-6 space-y-3">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-start space-x-2">
                      <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-900 mb-2">
                          전화 예약 안내
                        </p>
                        <p className="text-sm text-blue-700 leading-relaxed mb-3">
                          이 숙소는 전화로 직접 예약하실 수 있습니다.
                        </p>
                        {accommodation.contact_phone && (
                          <a
                            href={`tel:${accommodation.contact_phone}`}
                            className="inline-flex items-center justify-center w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {accommodation.contact_phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Course Card */}
              {relatedCourse && (
                <div className="bg-gradient-to-br from-[#F39800]/5 to-[#F39800]/10 rounded-2xl p-6 shadow-sm border border-[#F39800]/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      <Footprints className="w-5 h-5 text-[#F39800] mr-2" />
                      인근 둘레길
                    </h3>
                  </div>

                  <Link
                    to={`/course-detail/${relatedCourse.course_id}`}
                    className="block hover:scale-[1.02] transition-transform duration-200"
                  >
                    <div className="space-y-3">
                      {/* Trail Badge */}
                      <Badge className="bg-[#F39800]/10 text-[#F39800] border-[#F39800]/20 hover:bg-[#F39800]/20">
                        {relatedCourse.trail_name}
                      </Badge>

                      {/* Course Name */}
                      <h4 className="text-xl font-bold text-gray-900">
                        {relatedCourse.name}
                      </h4>

                      {/* Course Stats */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/50 rounded-lg p-3">
                          <div className="flex items-center text-xs text-gray-600 mb-1">
                            <MapPin className="w-3 h-3 mr-1" />
                            <span>거리</span>
                          </div>
                          <p className="text-sm font-bold text-gray-900">
                            {formatDistance(relatedCourse.length)}
                          </p>
                        </div>

                        <div className="bg-white/50 rounded-lg p-3">
                          <div className="flex items-center text-xs text-gray-600 mb-1">
                            <Clock className="w-3 h-3 mr-1" />
                            <span>소요시간</span>
                          </div>
                          <p className="text-sm font-bold text-gray-900">
                            {formatDuration(relatedCourse.estimated_time)}
                          </p>
                        </div>
                      </div>

                      {/* Difficulty */}
                      {relatedCourse.difficulty && (
                        <div className="flex items-center justify-between bg-white/50 rounded-lg p-3">
                          <span className="text-xs text-gray-600">난이도</span>
                          <span className="text-sm font-bold text-gray-900">
                            {relatedCourse.difficulty}
                          </span>
                        </div>
                      )}

                      {/* Description */}
                      {relatedCourse.short_desc && (
                        <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
                          {relatedCourse.short_desc.split('\n')[0]}
                        </p>
                      )}

                      {/* CTA */}
                      <div className="flex items-center justify-center text-[#F39800] font-semibold text-sm pt-2">
                        코스 자세히 보기
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile Info Card */}
        {isMobile && (
          <div className="mt-6 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">숙소 정보</h3>

            <div className="space-y-4">
              {/* Price */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600 font-medium">1박 요금</span>
                <span className="text-xl font-bold text-[#F39800]">
                  {formatPrice(accommodation.price_from)}
                </span>
              </div>
            </div>

            {/* Phone Booking Notice */}
            <div className="mt-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-start space-x-2">
                  <Phone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-2">
                      전화 예약 안내
                    </p>
                    <p className="text-sm text-blue-700 leading-relaxed mb-3">
                      이 숙소는 전화로 직접 예약하실 수 있습니다.
                    </p>
                    {accommodation.contact_phone && (
                      <a
                        href={`tel:${accommodation.contact_phone}`}
                        className="inline-flex items-center justify-center w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        {accommodation.contact_phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Width Content Sections - Below the 2-column grid */}
        <div className="mt-12 space-y-8">
          {/* Rooms Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">객실</h2>
            <RoomsTab accommodation={accommodation} />
          </div>

          {/* Location Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">위치</h2>
            <LocationTab accommodation={accommodation} />
          </div>

          {/* Reviews Section - Hidden for now */}
          {/* <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">리뷰</h2>
            <ReviewsTab accommodation={accommodation} />
          </div> */}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccommodationDetailPage;
